import React, { useMemo, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { action } from 'mobx';
import { useHistory } from 'react-router';
import { startCase } from 'lodash';

import { Button, Display } from 'Views/Components/Button/Button';
import { TextField } from 'Views/Components/TextBox/TextBox';
import FilterContainer from 'Views/Components/FilterContainer/FilterContainer';
import StaffList from 'Views/Components/StaffList/StaffList';
import { DatePicker } from 'Views/Components/DatePicker/DatePicker';
import { DisplayType } from 'Views/Components/Models/Enums';
import { getUserFilters, IUserFilterState } from 'Views/Pages/Frontend/UserManagement/FilterHelpers';

import { userModelDisplayNameMap } from 'Models/Enums';
import useDebounce from 'Hooks/useDebounce';
import { UserStatus } from 'Util/UserUtils';
import { hasAnyRole } from 'Util/SecurityUtils';
import { Select } from 'Views/Components/Select/Select';
import useStore from 'Hooks/useStore';

const StaffListWithFilters = observer(() => {
	const history = useHistory();
	const store = useStore();
	const [searchValue, setSearchValue] = useState<string>();
	const filterModel = useLocalStore<IUserFilterState>(() => ({}));

	const [showFilters, setFilterVisibility] = useState(false);
	const searchFieldStore = useLocalStore(() => ({ value: undefined }));

	const currentSearchFilterParams = useMemo(
		() => getUserFilters(filterModel),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[filterModel.role, filterModel.status, filterModel.createdAfter, filterModel.createdBefore],
	);

	const resetFilters = action(() => {
		filterModel.role = undefined;
		filterModel.status = undefined;
		filterModel.createdAfter = undefined;
		filterModel.createdBefore = undefined;
	});

	const setSearchDebounced = useDebounce(
		(abortSignals, newSearchValue) => setSearchValue(newSearchValue),
		[],
	);

	const includeRoleFilter = (discriminator: string) => {
		const discriminatorType = discriminator as keyof typeof userModelDisplayNameMap;

		return hasAnyRole(store, ['Admin'])
			? (
				discriminatorType !== 'User'
				&& discriminatorType !== 'UserEntity'
			)
			: (
				discriminatorType !== 'User'
				&& discriminatorType !== 'UserEntity'
				&& discriminatorType !== 'AdminEntity'
			);
	};

	const roleFilterOptions = Object.keys(userModelDisplayNameMap)
		.filter(includeRoleFilter)
		.map(discriminator => ({
			display: userModelDisplayNameMap[discriminator as keyof typeof userModelDisplayNameMap],
			value: discriminator,
		}))
		.sort((a, b) => a.display.localeCompare(b.display));

	const statusFilterOptions = Object.keys(UserStatus)
		.map(status => ({
			display: startCase(status.toLowerCase()),
			value: status,
		}));

	return (
		<div className="filtered-staff-list">
			<div className="list-row">
				<div className="search-bar">
					<TextField
						className="search-input"
						placeholder="Search staff"
						model={searchFieldStore}
						modelProperty="value"
						onAfterChange={evt => setSearchDebounced(evt.target.value)}
					/>
				</div>
				<div className="search-actions">
					<Button
						display={showFilters ? Display.Outline : Display.Solid}
						icon={{ icon: 'filter-list', iconPos: 'icon-right' }}
						onClick={() => setFilterVisibility(prev => !prev)}
						testId="staff-list-collection-search-filter-btn"
					>
						Filter
					</Button>
					{
						hasAnyRole(store, ['Admin', 'InstitutionAdmin'])
							? (
								<Button
									display={Display.Solid}
									icon={{ icon: 'add', iconPos: 'icon-right' }}
									onClick={() => history.push('users/staff/create')}
								>
									Add staff
								</Button>
							) : null
					}
				</div>
			</div>
			<div className="list-row">
				<div className="filters">
					<FilterContainer
						showFilters={showFilters}
						onClear={resetFilters}
					>
						<Select
							labelVisible={false}
							label="Role"
							placeholder="Role"
							model={filterModel}
							modelProperty="role"
							options={roleFilterOptions}
							displayType={DisplayType.INLINE}
							inputProps={{
								'aria-label': 'Role',
							}}
						/>
						<Select
							labelVisible={false}
							label="Status"
							placeholder="Status"
							model={filterModel}
							modelProperty="status"
							options={statusFilterOptions}
							displayType={DisplayType.INLINE}
							inputProps={{
								'aria-label': 'Status',
							}}
						/>
						<DatePicker
							model={filterModel}
							modelProperty="createdAfter"
							name="CreatedAfter"
							label="Created after"
							placeholder="Created after"
							labelVisible={false}
							displayType={DisplayType.INLINE}
						/>
						<DatePicker
							model={filterModel}
							modelProperty="createdBefore"
							name="CreatedBefore"
							label="Created before"
							placeholder="Created before"
							labelVisible={false}
							displayType={DisplayType.INLINE}
						/>
					</FilterContainer>
				</div>
			</div>
			<div className="list-row">
				<StaffList
					searchTerm={searchValue}
					filters={currentSearchFilterParams}
				/>
			</div>
		</div>
	);
});

export default StaffListWithFilters;
